import React, { useState, useEffect, useCallback } from 'react'
import {
  Autocomplete, Backdrop, Box, Chip, CircularProgress, Container,
  List, ListItem, ListItemText, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip as MuiTooltip, Typography,
} from '@mui/material'
import { ThemeProvider } from '@emotion/react'
import {
  AreaChart, Area, Bar, BarChart, Cell, LabelList, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import MUIDataTable from 'mui-datatables'
import moment from 'moment'
import { blueGrey, blue, green, grey, orange, red } from '@mui/material/colors'
import theme from '../themeDash'
import api from '../services/api'
import KPICard from '../Report/KPICard'

const BAR_COLORS = [
  "#e3f9ad", "#90caf9", "#4caf50", "#1976d2", "#0d47a1", "#f44336", "#fdca9a", "#388e3c", "#2196f3", "#1b5e20",
  "#ffebee", "#ef9a9a", "#d32f2f", "#ff9800", "#b71c1c", "#00f3e0", "#ffcc80", "#bf360c", "#a5d6a7", "#7b1fa2",
  "#f57c00", "#e91e63", "#fbe9e7", "#ffab91", "#ff5722", "#bcaaa4", "#e64a19", "#5d4037", "#ce93d8", "#4a148c",
]

function ChartBox({ title, children, width = 600, height = 400 }) {
  return (
    <Box sx={{ bgcolor: blueGrey[900], m: 1, p: 2, boxShadow: 3, borderRadius: 1 }}>
      <Typography variant="h6" sx={{ color: grey[100], mb: 1, textAlign: 'center' }}>
        {title}
      </Typography>
      <ResponsiveContainer width={width} height={height}>
        {children}
      </ResponsiveContainer>
    </Box>
  )
}

// ─── Tab 0: Visao Geral ──────────────────────────────────────
function TabVisaoGeral({ kpis, empresasData, loading }) {
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
  if (!kpis) return null

  const timeline = (kpis.timeline || []).map(t => ({
    ...t,
    zabbix: t.by_source?.zabbix || 0,
    wazuh: t.by_source?.wazuh || 0,
  }))

  const empresas = (empresasData?.results?.empresa || [])
    .sort((a, b) => b.qtd_problems - a.qtd_problems)
    .map((e, i) => ({
      ...e,
      fill: BAR_COLORS[i % BAR_COLORS.length],
      mtta: Math.round(e.mean_react_time / 60),
      mttr: Math.round(e.mean_resolve_time / 60),
    }))

  return (
    <Box>
      {/* KPI Cards */}
      <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
        <KPICard title="Total Alarmes" value={kpis.comparison?.current?.total} change={kpis.comparison?.variation?.total_pct} />
        <KPICard title="MTTA" value={kpis.comparison?.current?.mtta} unit="min" change={kpis.comparison?.variation?.mtta_pct} />
        <KPICard title="MTTR" value={kpis.comparison?.current?.mttr} unit="min" change={kpis.comparison?.variation?.mttr_pct} />
        <KPICard title="Resolvidos" value={kpis.comparison?.current?.resolved_pct} unit="%" change={kpis.comparison?.variation?.resolved_pct} invertColors />
      </Box>

      {/* Timeline AreaChart */}
      {timeline.length > 0 && (
        <Box sx={{ width: '100%', mt: 3, bgcolor: blueGrey[900], p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="h6" sx={{ color: grey[100], mb: 1, textAlign: 'center' }}>
            Timeline de Alarmes
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={timeline}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: grey[400] }} stroke={grey[600]}
                tickFormatter={v => v.length > 10 ? v.slice(11, 16) : v.slice(5)} />
              <YAxis stroke={grey[600]} tick={{ fill: grey[400] }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="zabbix" stackId="1" fill={blue[600]} stroke={blue[600]} name="Zabbix" />
              <Area type="monotone" dataKey="wazuh" stackId="1" fill={orange[600]} stroke={orange[600]} name="Wazuh" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Empresa Ranking Table */}
      {empresas.length > 0 && (
        <Box sx={{ mt: 3, bgcolor: blueGrey[900], p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="h6" sx={{ color: grey[100], mb: 1, textAlign: 'center' }}>
            Ranking por Empresa
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Empresa', 'Total', 'Acks', 'MTTA (min)', 'MTTR (min)'].map(col => (
                    <TableCell key={col} align={col === 'Empresa' ? 'left' : 'right'}
                      sx={{ color: grey[400], borderBottom: `1px solid ${grey[700]}` }}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {empresas.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ color: grey[200], borderBottom: `1px solid ${grey[800]}` }}>{e.cod_empresa}</TableCell>
                    <TableCell sx={{ color: grey[200], borderBottom: `1px solid ${grey[800]}` }} align="right">{e.qtd_problems}</TableCell>
                    <TableCell sx={{ color: grey[200], borderBottom: `1px solid ${grey[800]}` }} align="right">{e.qtd_acks}</TableCell>
                    <TableCell sx={{ color: grey[200], borderBottom: `1px solid ${grey[800]}` }} align="right">{e.mtta}</TableCell>
                    <TableCell sx={{ color: grey[200], borderBottom: `1px solid ${grey[800]}` }} align="right">{e.mttr}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Bar Chart Comparativo */}
      {empresas.length > 0 && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
          <ChartBox title="Alarmes por Empresa">
            <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
              <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
              <YAxis stroke={grey[400]} />
              <Tooltip />
              <Bar dataKey="qtd_problems" name="Total">
                {empresas.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                <LabelList dataKey="qtd_problems" position="top" fill={grey[100]} />
              </Bar>
            </BarChart>
          </ChartBox>
          <ChartBox title="MTTA / MTTR por Empresa (min)">
            <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
              <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
              <YAxis stroke={grey[400]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="mtta" fill={orange[600]} name="MTTA">
                <LabelList dataKey="mtta" position="top" fill={grey[100]} />
              </Bar>
              <Bar dataKey="mttr" fill={red[600]} name="MTTR">
                <LabelList dataKey="mttr" position="top" fill={grey[100]} />
              </Bar>
            </BarChart>
          </ChartBox>
        </Box>
      )}
    </Box>
  )
}

// ─── Tab 1: Equipe ───────────────────────────────────────────
function TabEquipe({ data, comparison, loading, onTecnicoClick, individualData, selectedTecnico, individualLoading }) {
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
  if (!data) return null

  const global = data?.results?.global || {}
  const tecnicas = (data?.results?.tecnica || []).map((obj, i) => ({ ...obj, fill: BAR_COLORS[i % BAR_COLORS.length] }))
  const pieData = Object.entries(global).map(([key, val]) => ({
    name: key === "seen" ? "Visto" : "Nao visto",
    fill: key === "seen" ? green[500] : red[500],
    value: Math.round(val / Object.values(global).reduce((a, b) => a + b, 0) * 100),
  }))

  const totalVistados = tecnicas.reduce((acc, t) => acc + t.qtd_problems, 0)
  const avgReact = tecnicas.length > 0
    ? Math.round(tecnicas.reduce((acc, t) => acc + t.mean_react_time, 0) / tecnicas.length / 60)
    : 0

  return (
    <Box>
      {comparison && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
          <KPICard title="Total Vistados" value={totalVistados} change={comparison.variation?.total_pct} />
          <KPICard title="MTTA Equipe" value={avgReact} unit="min" change={comparison.variation?.mtta_pct} />
          <KPICard title="Tecnicos Ativos" value={tecnicas.length} />
          <KPICard title="Resolvidos" value={comparison.current?.resolved_pct} unit="%" change={comparison.variation?.resolved_pct} invertColors />
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" justifyContent="center" sx={{ mt: 2 }}>
        <ChartBox title="Problemas por Tecnico">
          <BarChart data={tecnicas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="visted_by" stroke={grey[400]} />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="qtd_problems" onClick={(entry) => onTecnicoClick && onTecnicoClick(entry.visted_by)} cursor="pointer">
              {tecnicas.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="qtd_problems" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="Tempo de Reacao (min)">
          <BarChart
            data={tecnicas.map(obj => ({
              ...obj,
              visted_by: obj.visted_by + " (" + obj.qtd_problems + ")",
              min_react_time: Math.round(obj.min_react_time / 60),
              mean_react_time: Math.round(obj.mean_react_time / 60),
              max_react_time: Math.round(obj.max_react_time / 60),
            }))}
            margin={{ top: 30, right: 30, left: 20, bottom: 70 }}
          >
            <Legend verticalAlign="bottom" height={40}
              payload={tecnicas.map(obj => ({ value: obj.visted_by, type: "rect", id: obj.visted_by, color: obj.fill }))}
            />
            <XAxis dataKey="visted_by" stroke={grey[400]} />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="min_react_time" fill={blue[600]} name="Minimo">
              <LabelList dataKey="min_react_time" position="top" fill={grey[100]} />
            </Bar>
            <Bar dataKey="mean_react_time" fill={orange[600]} name="Media">
              <LabelList dataKey="mean_react_time" position="top" fill={grey[100]} />
            </Bar>
            <Bar dataKey="max_react_time" fill={red[900]} name="Maximo">
              <LabelList dataKey="max_react_time" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="Alarmes" width={400} height={300}>
          <PieChart>
            <Pie data={pieData} innerRadius={80} outerRadius={110} stroke={blueGrey[900]} strokeWidth={2}
              label={({ name, value }) => `${name} ${value}%`}
            />
          </PieChart>
        </ChartBox>
      </Box>

      {/* Individual Analysis */}
      {selectedTecnico && (
        <Box sx={{ width: '100%', mt: 3 }}>
          <Typography variant="h5" sx={{ color: grey[100], textAlign: 'center', mb: 2 }}>
            Analise Individual — {selectedTecnico}
          </Typography>

          {individualLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
          ) : individualData ? (
            <Box display="flex" flexWrap="wrap" justifyContent="center">
              <Box display="flex" gap={2} justifyContent="center" width="100%" mb={2}>
                <Chip label={`Total vistados: ${individualData.total}`} color="primary" />
                <Chip label={`MTTA: ${individualData.mean_react_time} min`} color="warning" />
              </Box>

              {individualData.timeline?.length > 0 && (
                <ChartBox title="Acks por Dia">
                  <BarChart data={individualData.timeline} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <XAxis dataKey="date" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
                    <YAxis stroke={grey[400]} />
                    <Tooltip />
                    <Bar dataKey="count" fill={blue[600]}>
                      <LabelList dataKey="count" position="top" fill={grey[100]} />
                    </Bar>
                  </BarChart>
                </ChartBox>
              )}

              {individualData.trend?.length > 0 && (
                <ChartBox title="MTTA Trend (min)">
                  <LineChart data={individualData.trend} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <XAxis dataKey="date" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
                    <YAxis stroke={grey[400]} />
                    <Tooltip />
                    <Line type="monotone" dataKey="mean_react_time" stroke={orange[500]} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ChartBox>
              )}

              {individualData.top_alarms?.length > 0 && (
                <Box sx={{ bgcolor: blueGrey[900], p: 2, m: 1, borderRadius: 1, minWidth: 350, boxShadow: 3 }}>
                  <Typography variant="h6" sx={{ color: grey[100], mb: 1 }}>Top 5 Alarmes Vistados</Typography>
                  <List dense>
                    {individualData.top_alarms.map((alarm, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          primary={alarm.name}
                          secondary={`${alarm.count} vezes`}
                          primaryTypographyProps={{ sx: { color: grey[200], fontSize: 13 } }}
                          secondaryTypographyProps={{ sx: { color: grey[400] } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  )
}

// ─── Tab 2: Empresas ─────────────────────────────────────────
function TabEmpresas({ data, loading, period }) {
  const [selectedEmpresa, setSelectedEmpresa] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const empresas = (data?.results?.empresa || [])
    .sort((a, b) => b.qtd_problems - a.qtd_problems)
    .map((e, i) => ({
      ...e,
      fill: BAR_COLORS[i % BAR_COLORS.length],
      mtta: Math.round(e.mean_react_time / 60),
      mttr: Math.round(e.mean_resolve_time / 60),
    }))

  const global = data?.results?.global || {}
  const comparison = data?.comparison
  const heatmap = data?.heatmap || []

  const pieData = [
    { name: 'Visto', value: global.seen || 0, fill: green[500] },
    { name: 'Nao visto', value: global.not_seen || 0, fill: red[500] },
  ].filter(d => d.value > 0)

  // Heatmap grid
  const empresaCodes = [...new Set(heatmap.map(h => h.cod_empresa))]
  const dates = [...new Set(heatmap.map(h => h.date))].sort()
  const maxCount = Math.max(...heatmap.map(h => h.count), 1)

  const handleEmpresaClick = (cod) => {
    setSelectedEmpresa(cod)
    setDetailLoading(true)
    api.get(`/dashboard/empresa-detail/${cod}?period=${period}`)
      .then(res => setDetail(res.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false))
  }

  useEffect(() => {
    setSelectedEmpresa(null)
    setDetail(null)
  }, [data])

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
  if (!data) return null

  return (
    <Box>
      {/* KPIs */}
      {comparison && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
          <KPICard title="Total Alarmes" value={comparison.current?.total} change={comparison.variation?.total_pct} />
          <KPICard title="MTTA" value={comparison.current?.mtta} unit="min" change={comparison.variation?.mtta_pct} />
          <KPICard title="MTTR" value={comparison.current?.mttr} unit="min" change={comparison.variation?.mttr_pct} />
          <KPICard title="Resolvidos" value={comparison.current?.resolved_pct} unit="%" change={comparison.variation?.resolved_pct} invertColors />
        </Box>
      )}

      {/* Filtro empresa */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Autocomplete
          options={empresas.map(e => e.cod_empresa)}
          value={selectedEmpresa}
          onChange={(_, val) => val ? handleEmpresaClick(val) : (setSelectedEmpresa(null), setDetail(null))}
          sx={{ width: 300 }}
          renderInput={(params) => <TextField {...params} label="Filtrar Empresa" size="small" />}
        />
      </Box>

      {/* Charts */}
      <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
        <ChartBox title="Alarmes por Empresa">
          <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="qtd_problems" cursor="pointer"
              onClick={(entry) => handleEmpresaClick(entry.cod_empresa)}>
              {empresas.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="qtd_problems" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="Acks por Empresa">
          <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="qtd_acks" cursor="pointer"
              onClick={(entry) => handleEmpresaClick(entry.cod_empresa)}>
              {empresas.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="qtd_acks" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="MTTA por Empresa (min)">
          <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="mtta" fill={orange[600]}>
              <LabelList dataKey="mtta" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="MTTR por Empresa (min)">
          <BarChart data={empresas} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="mttr" fill={red[600]}>
              <LabelList dataKey="mttr" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        {pieData.length > 0 && (
          <ChartBox title="Alarmes Vistados" width={400} height={300}>
            <PieChart>
              <Pie data={pieData} innerRadius={80} outerRadius={110} stroke={blueGrey[900]} strokeWidth={2}
                label={({ name, value }) => `${name} ${value}`} />
            </PieChart>
          </ChartBox>
        )}
      </Box>

      {/* Heatmap */}
      {heatmap.length > 0 && (
        <Box sx={{ mt: 2, bgcolor: blueGrey[900], p: 2, boxShadow: 3, borderRadius: 1, overflowX: 'auto' }}>
          <Typography variant="h6" sx={{ color: grey[100], mb: 1, textAlign: 'center' }}>
            Heatmap Empresa x Dia
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Header row */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box sx={{ width: 80, flexShrink: 0 }} />
              {dates.map(d => (
                <MuiTooltip key={d} title={d}>
                  <Box sx={{ width: 24, height: 20, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 8, color: grey[500] }}>{d.slice(8)}</Typography>
                  </Box>
                </MuiTooltip>
              ))}
            </Box>
            {/* Data rows */}
            {empresaCodes.map(cod => (
              <Box key={cod} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', cursor: 'pointer' }}
                onClick={() => handleEmpresaClick(cod)}>
                <Typography sx={{ width: 80, fontSize: 11, color: grey[300], flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cod}
                </Typography>
                {dates.map(d => {
                  const cell = heatmap.find(h => h.cod_empresa === cod && h.date === d)
                  const count = cell?.count || 0
                  const intensity = count / maxCount
                  return (
                    <MuiTooltip key={d} title={`${cod} — ${d}: ${count}`}>
                      <Box sx={{
                        width: 24, height: 20, borderRadius: 0.5,
                        bgcolor: count === 0 ? grey[800] : `rgba(244, 67, 54, ${0.2 + intensity * 0.8})`,
                      }} />
                    </MuiTooltip>
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Drill-down */}
      {selectedEmpresa && (
        <Box sx={{ mt: 3, bgcolor: blueGrey[800], p: 2, borderRadius: 1, boxShadow: 3 }}>
          <Typography variant="h5" sx={{ color: grey[100], textAlign: 'center', mb: 2 }}>
            Detalhe — Empresa {selectedEmpresa}
          </Typography>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
          ) : detail ? (
            <Box>
              <Box display="flex" flexWrap="wrap" justifyContent="center">
                <KPICard title="Total" value={detail.kpis?.total} />
                <KPICard title="MTTA" value={detail.kpis?.mtta} unit="min" />
                <KPICard title="MTTR" value={detail.kpis?.mttr} unit="min" />
                <KPICard title="Resolvidos" value={detail.kpis?.resolved_pct} unit="%" />
              </Box>
              {detail.top_hosts?.length > 0 && (
                <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
                  <ChartBox title="Top Hosts — Alarmes">
                    <BarChart data={detail.top_hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
                      <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
                      <YAxis stroke={grey[400]} />
                      <Bar dataKey="qtd_problems" fill={blue[600]}>
                        <LabelList dataKey="qtd_problems" position="top" fill={grey[100]} />
                      </Bar>
                    </BarChart>
                  </ChartBox>
                  <ChartBox title="Top Hosts — Acks">
                    <BarChart data={detail.top_hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
                      <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
                      <YAxis stroke={grey[400]} />
                      <Bar dataKey="qtd_acks" fill={green[600]}>
                        <LabelList dataKey="qtd_acks" position="top" fill={grey[100]} />
                      </Bar>
                    </BarChart>
                  </ChartBox>
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  )
}

// ─── Tab 3: Hosts ────────────────────────────────────────────
function TabHosts({ data, loading }) {
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
  if (!data) return null

  const hosts = (data?.results?.host || []).map((h, i) => ({
    ...h,
    fill: BAR_COLORS[i % BAR_COLORS.length],
    mtta: Math.round(h.mean_react_time / 60),
    mttr: Math.round(h.mean_resolve_time / 60),
  }))

  const global = data?.results?.global || {}
  const comparison = data?.comparison

  const pieData = [
    { name: 'Visto', value: global.seen || 0, fill: green[500] },
    { name: 'Nao visto', value: global.not_seen || 0, fill: red[500] },
  ].filter(d => d.value > 0)

  return (
    <Box>
      {comparison && (
        <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
          <KPICard title="Total Alarmes" value={comparison.current?.total} change={comparison.variation?.total_pct} />
          <KPICard title="MTTA" value={comparison.current?.mtta} unit="min" change={comparison.variation?.mtta_pct} />
          <KPICard title="MTTR" value={comparison.current?.mttr} unit="min" change={comparison.variation?.mttr_pct} />
          <KPICard title="Resolvidos" value={comparison.current?.resolved_pct} unit="%" change={comparison.variation?.resolved_pct} invertColors />
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
        <ChartBox title="Top 10 Hosts — Alarmes">
          <BarChart data={hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="qtd_problems">
              {hosts.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="qtd_problems" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="Top 10 Hosts — Acks">
          <BarChart data={hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="qtd_acks" fill={green[600]}>
              <LabelList dataKey="qtd_acks" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="MTTA por Host (min)">
          <BarChart data={hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="mtta" fill={orange[600]}>
              <LabelList dataKey="mtta" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="MTTR por Host (min)">
          <BarChart data={hosts} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="hostname" stroke={grey[400]} tick={{ fontSize: 9 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="mttr" fill={red[600]}>
              <LabelList dataKey="mttr" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        {pieData.length > 0 && (
          <ChartBox title="Alarmes Vistados" width={400} height={300}>
            <PieChart>
              <Pie data={pieData} innerRadius={80} outerRadius={110} stroke={blueGrey[900]} strokeWidth={2}
                label={({ name, value }) => `${name} ${value}`} />
            </PieChart>
          </ChartBox>
        )}
      </Box>
    </Box>
  )
}

// ─── Tab 4: Acks ─────────────────────────────────────────────
function TabAcks({ overview, detail, loading, detailLoading, page, onPageChange }) {
  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>

  const byUser = (overview?.by_user || []).map((u, i) => ({ ...u, fill: BAR_COLORS[i % BAR_COLORS.length] }))
  const timeline = overview?.timeline || []
  const byEmpresa = (overview?.by_empresa || []).map((e, i) => ({ ...e, fill: BAR_COLORS[i % BAR_COLORS.length] }))

  const columns = [
    {
      name: 'source',
      label: 'Fonte',
      options: {
        customBodyRender: (value) => (
          <Chip label={value || 'zabbix'} size="small"
            sx={{ bgcolor: (value || 'zabbix') === 'zabbix' ? blue[700] : orange[700], color: '#fff', fontWeight: 600 }} />
        ),
      },
    },
    { name: 'cod_empresa', label: 'Empresa' },
    { name: 'username', label: 'Usuario' },
    { name: 'description', label: 'Descricao' },
    { name: 'detail', label: 'Detalhes' },
    {
      name: 'created_at',
      label: 'Horario',
      options: {
        customBodyRender: (value) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
      },
    },
  ]

  const options = {
    filterType: 'textField',
    selectableRows: 'none',
    rowsPerPage: 30,
    rowsPerPageOptions: [30],
    serverSide: true,
    count: detail?.total || 0,
    page: page - 1,
    onTableChange: (action, tableState) => {
      if (action === 'changePage') onPageChange(tableState.page + 1)
    },
    textLabels: {
      body: { noMatch: 'Nenhum registro encontrado' },
      pagination: { rowsPerPage: 'Linhas por pag.' },
    },
  }

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" justifyContent="center" mt={2}>
        <ChartBox title="Acks por Tecnico">
          <BarChart data={byUser} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="username" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="count">
              {byUser.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="count" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>

        <ChartBox title="Acks por Empresa">
          <BarChart data={byEmpresa} margin={{ top: 30, right: 30, left: 20, bottom: 70 }}>
            <XAxis dataKey="cod_empresa" stroke={grey[400]} tick={{ fontSize: 10 }} angle={-45} textAnchor="end" />
            <YAxis stroke={grey[400]} />
            <Bar dataKey="count">
              {byEmpresa.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
              <LabelList dataKey="count" position="top" fill={grey[100]} />
            </Bar>
          </BarChart>
        </ChartBox>
      </Box>

      {/* Timeline */}
      {timeline.length > 0 && (
        <Box sx={{ width: '100%', mt: 2, bgcolor: blueGrey[900], p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="h6" sx={{ color: grey[100], mb: 1, textAlign: 'center' }}>
            Timeline de Acks
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={timeline}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: grey[400] }} stroke={grey[600]}
                tickFormatter={v => v.length > 10 ? v.slice(11, 16) : v.slice(5)} />
              <YAxis stroke={grey[600]} tick={{ fill: grey[400] }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="zabbix" stackId="1" fill={blue[600]} stroke={blue[600]} name="Zabbix" />
              <Area type="monotone" dataKey="wazuh" stackId="1" fill={orange[600]} stroke={orange[600]} name="Wazuh" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Detail Table */}
      <Box sx={{ mt: 3 }}>
        {detailLoading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress color="info" /></Box>
        ) : (
          <MUIDataTable
            title="Acks Detalhado"
            data={detail?.results || []}
            columns={columns}
            options={options}
          />
        )}
      </Box>
    </Box>
  )
}

// ─── Main Component ──────────────────────────────────────────
export default function PainelNOC() {
  const [tab, setTab] = useState(0)
  const [period, setPeriod] = useState('24h')
  const [loading, setLoading] = useState(false)

  // Tab 0: Visao Geral
  const [kpis, setKpis] = useState(null)

  // Tab 1: Equipe
  const [equipeData, setEquipeData] = useState(null)
  const [equipeComparison, setEquipeComparison] = useState(null)
  const [individualData, setIndividualData] = useState(null)
  const [selectedTecnico, setSelectedTecnico] = useState(null)
  const [individualLoading, setIndividualLoading] = useState(false)
  const equipeRef = React.useRef(null)

  // Tab 2: Empresas (shared with Tab 0)
  const [empresasData, setEmpresasData] = useState(null)

  // Tab 3: Hosts
  const [hostsData, setHostsData] = useState(null)

  // Tab 4: Acks
  const [acksOverview, setAcksOverview] = useState(null)
  const [acksDetail, setAcksDetail] = useState(null)
  const [acksDetailLoading, setAcksDetailLoading] = useState(false)
  const [acksPage, setAcksPage] = useState(1)

  const fetchVisaoGeral = useCallback(() => {
    setLoading(true)
    Promise.all([
      api.get(`/dashboard/kpis/?period=${period}`),
      api.get(`/dashboard/empresas/?period=${period}`),
    ])
      .then(([kpisRes, empRes]) => {
        setKpis(kpisRes.data)
        setEmpresasData(empRes.data)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  const fetchEquipe = useCallback(() => {
    setLoading(true)
    setIndividualData(null)
    setSelectedTecnico(null)
    api.get(`/dashboard/equipe/?period=${period}`)
      .then(res => {
        setEquipeData(res.data)
        setEquipeComparison(res.data.comparison)
        equipeRef.current = {
          empresas: res.data.empresas,
          start: res.data.start,
          end: res.data.end,
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  const fetchEmpresas = useCallback(() => {
    setLoading(true)
    api.get(`/dashboard/empresas/?period=${period}`)
      .then(res => setEmpresasData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  const fetchHosts = useCallback(() => {
    setLoading(true)
    api.get(`/dashboard/hosts/?period=${period}`)
      .then(res => setHostsData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  const fetchAcks = useCallback(() => {
    setLoading(true)
    setAcksPage(1)
    Promise.all([
      api.get(`/dashboard/acks-overview/?period=${period}`),
      api.get(`/dashboard/acks-detail/?period=${period}&page=1&limit=30`),
    ])
      .then(([overviewRes, detailRes]) => {
        setAcksOverview(overviewRes.data)
        setAcksDetail(detailRes.data)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  const fetchAcksPage = (newPage) => {
    setAcksPage(newPage)
    setAcksDetailLoading(true)
    api.get(`/dashboard/acks-detail/?period=${period}&page=${newPage}&limit=30`)
      .then(res => setAcksDetail(res.data))
      .catch(() => {})
      .finally(() => setAcksDetailLoading(false))
  }

  const fetchIndividual = (userName) => {
    if (!equipeRef.current) return
    setSelectedTecnico(userName)
    setIndividualLoading(true)
    api.post('/report/alarmes/tecnica/individual/', {
      user_name: userName,
      empresas: equipeRef.current.empresas,
      start: equipeRef.current.start,
      end: equipeRef.current.end,
    })
      .then(res => setIndividualData(res.data))
      .finally(() => setIndividualLoading(false))
  }

  // Fetch on mount and period/tab change
  useEffect(() => {
    if (tab === 0) fetchVisaoGeral()
    else if (tab === 1) fetchEquipe()
    else if (tab === 2) fetchEmpresas()
    else if (tab === 3) fetchHosts()
    else if (tab === 4) fetchAcks()
  }, [period, tab, fetchVisaoGeral, fetchEquipe, fetchEmpresas, fetchHosts, fetchAcks])

  // Auto-refresh 60s
  useEffect(() => {
    const interval = setInterval(() => {
      if (tab === 0) fetchVisaoGeral()
      else if (tab === 1) fetchEquipe()
      else if (tab === 2) fetchEmpresas()
      else if (tab === 3) fetchHosts()
      else if (tab === 4) fetchAcks()
    }, 60 * 1000)
    return () => clearInterval(interval)
  }, [tab, fetchVisaoGeral, fetchEquipe, fetchEmpresas, fetchHosts, fetchAcks])

  useEffect(() => {
    document.title = "Horus - Painel NOC"
  }, [])

  const handlePeriod = (_, val) => {
    if (val) setPeriod(val)
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
      <ThemeProvider theme={theme}>
        <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Header: Tabs + Period */}
        <Box display="flex" alignItems="center" justifyContent="space-between"
          sx={{ bgcolor: 'background.paper', p: 1, boxShadow: 1, borderRadius: 1 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary"
            variant="scrollable" scrollButtons="auto">
            <Tab label="Visao Geral" />
            <Tab label="Equipe" />
            <Tab label="Empresas" />
            <Tab label="Hosts" />
            <Tab label="Acks" />
          </Tabs>
          <ToggleButtonGroup value={period} exclusive onChange={handlePeriod} size="small">
            <ToggleButton value="24h" sx={{ color: grey[300] }}>Ultimas 24h</ToggleButton>
            <ToggleButton value="7d" sx={{ color: grey[300] }}>Ultimos 7 dias</ToggleButton>
            <ToggleButton value="30d" sx={{ color: grey[300] }}>Ultimo mes</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Tab Content */}
        {tab === 0 && <TabVisaoGeral kpis={kpis} empresasData={empresasData} loading={loading} />}
        {tab === 1 && (
          <TabEquipe
            data={equipeData}
            comparison={equipeComparison}
            loading={loading}
            onTecnicoClick={fetchIndividual}
            individualData={individualData}
            selectedTecnico={selectedTecnico}
            individualLoading={individualLoading}
          />
        )}
        {tab === 2 && <TabEmpresas data={empresasData} loading={loading} period={period} />}
        {tab === 3 && <TabHosts data={hostsData} loading={loading} />}
        {tab === 4 && (
          <TabAcks
            overview={acksOverview}
            detail={acksDetail}
            loading={loading}
            detailLoading={acksDetailLoading}
            page={acksPage}
            onPageChange={fetchAcksPage}
          />
        )}
      </ThemeProvider>
    </Container>
  )
}
